<template>
    <section
        v-if="!singleRuleConfig.singleRule"
        class="segment-editor"
        role="group"
        :aria-label="$t('segmentEditor.Segment Filters')">
        <div
            v-if="!rules.length"
            class="segment-editor__empty-card">
            <pendo-empty-state
                :icon="{
                    type: 'pie-chart',
                }"
                :description="$t('segmentEditor.Current segment is Everyone. Add rules to create a segment.')">
                <template #actions>
                    <pendo-button
                        ref="addRule"
                        class="add-rule"
                        theme="app"
                        type="primary"
                        :disabled="readOnly"
                        prefix-icon="plus"
                        :label="$t('segmentEditor.Add rules')"
                        @click="addRule" />
                </template>
            </pendo-empty-state>
        </div>
        <template v-if="rules.length">
            <div
                v-for="(rule, ruleIndex) in rules"
                :key="rule.key"
                ref="ruleList"
                :data-rule-index="ruleIndex"
                class="segment-editor__rule">
                <div
                    class="segment-editor__rule-card"
                    :aria-label="$t('segmentEditor.Filter group {number}', { number: ruleIndex + 1 })">
                    <div v-pendo-tooltip="tooltipConfig(rule)">
                        <rule-editor
                            v-for="(or, orIndex) in rule.or"
                            :key="or.key"
                            :index="orIndex"
                            :rule-number="orIndex + 1"
                            :group-number="ruleIndex + 1"
                            :check-valid-rule="checkValidRule"
                            :config="config"
                            :read-only="!!(readOnly || or.isFocusGroup)"
                            :rule="or"
                            :segment="segment"
                            :segment-is-nested="isNested"
                            :has-dependent-segment="hasDependentSegment"
                            :is-draft-editor="isDraftEditor"
                            :can-edit-and-share="canEditAndShare"
                            @remove="removeOrRule(ruleIndex, orIndex)"
                            @input="updateOrRule($event, ruleIndex, orIndex)" />
                    </div>
                    <div
                        v-if="!hasFocusGroup(rule)"
                        class="segment-editor__add-or-rule-divider"
                        aria-hidden="true">
                        <pendo-divider
                            height="1px"
                            decorative
                            :stroke="dividerColor" />
                    </div>
                    <div
                        v-if="!hasFocusGroup(rule)"
                        class="segment-editor__add-or-rule">
                        <pendo-button
                            ref="addOrRule"
                            class="add-or-rule"
                            theme="app"
                            type="secondary"
                            :size="isDraftEditor ? 'mini' : 'medium'"
                            :disabled="readOnly"
                            prefix-icon="or"
                            :aria-label="$t('segmentEditor.Add OR condition to filter group {number}', { number: ruleIndex + 1 })"
                            :label="$t('segmentEditor.OR')"
                            @click="addOrRule(ruleIndex)" />
                    </div>
                </div>
                <div
                    v-if="rules.length > 1 && rules.length !== ruleIndex + 1"
                    class="segment-editor__rule-seperator">
                    {{ $t('segmentEditor.AND') }}
                    <pendo-divider
                        height="56px"
                        decorative
                        :stroke="dividerColor" />
                </div>
            </div>
            <div class="segment-editor__add-rule">
                <pendo-button
                    ref="addRule"
                    class="add-rule"
                    theme="app"
                    type="primary"
                    :size="isDraftEditor ? 'mini' : 'medium'"
                    :disabled="readOnly"
                    prefix-icon="plus"
                    :aria-label="$t('segmentEditor.Add new AND filter group')"
                    :label="!rules.length ? $t('segmentEditor.ADD') : $t('segmentEditor.AND')"
                    @click="addRule" />
            </div>
        </template>
    </section>
    <section
        v-else
        class="segment-editor"
        role="group"
        :aria-label="$t('segmentEditor.Segment Filters')">
        <div
            v-if="ready && hasRule"
            class="segment-editor__rule">
            <fieldset
                class="segment-editor__rule-card"
                :aria-label="$t('segmentEditor.Filter rule')">
                <legend class="visually-hidden">
                    {{ $t('segmentEditor.Filter rule') }}
                </legend>
                <div>
                    <rule-editor
                        :rule-number="1"
                        :group-number="1"
                        :check-valid-rule="checkValidRule"
                        :config="config"
                        :read-only="readOnly"
                        :rule="hasRule"
                        :segment="segment"
                        :segment-is-nested="isNested"
                        :has-dependent-segment="hasDependentSegment"
                        :is-draft-editor="isDraftEditor"
                        :can-edit-and-share="canEditAndShare"
                        @remove="removeSingleRule"
                        @input="updateOrRule($event, 0, 0)" />
                </div>
                <div class="segment-editor__single-rule-save-button">
                    <pendo-button
                        theme="app"
                        type="primary"
                        :label="$t('segmentEditor.Save')"
                        :disabled="singleRuleConfig.singleRuleSaveButtonDisabled"
                        @click="saveSingleRule" />
                </div>
            </fieldset>
        </div>
    </section>
</template>

<script>
import { PendoEmptyState, PendoButton, PendoDivider, PendoTooltip } from '@pendo/components';
import { getComputedColor } from '@utils/color';
import { mapGetters } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { v4 as uuid } from 'uuid';
import last from 'lodash/last';
import debounce from 'lodash/debounce';
import RuleEditor from '@/segments/components/segment-editor-vue/rule-editor.vue';
import {
    createEmptyRule,
    createEmptyRuleset,
    isRuleValid,
    convertRule,
    scrollIntoViewIfNeeded,
    removeSpecialChars,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { SEGMENT_RULE_LIMIT, APP_SPECIFIC_AUTO_METADATA_IDS, getAppLevelEventRules } from '@/utils/segments.js';

const RESPONSIVE_BREAKPOINT = 916;

export default {
    name: 'SegmentEditor',
    components: {
        PendoEmptyState,
        PendoDivider,
        PendoButton,
        RuleEditor,
    },
    directives: {
        PendoTooltip,
    },
    inject: ['segmentsUsedCount'],
    props: {
        segment: {
            type: Object,
            required: true,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        config: {
            type: Object,
            required: true,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isDraftEditor: {
            type: Boolean,
            required: false,
        },
        canEditAndShare: {
            type: Boolean,
            default: false,
        },
        hasDependentSegment: {
            type: Boolean,
            default: false,
        },
        singleRuleConfig: {
            type: Object,
            default: () => ({
                singleRule: false,
                singleRuleSaveButtonDisabled: false,
            }),
        },
    },
    emits: ['update', 'save', 'close'],
    data () {
        return {
            rules: [],
            ready: false,
            dividerColor: getComputedColor('--palette-gray-40'),
        };
    },
    computed: {
        ...mapGetters({
            parentSegmentsById: 'segments/parentSegmentsById',
        }),
        isNested () {
            return this.parentSegmentsById(this.segment?.id).length > 0;
        },
        hasRule () {
            return this.rules?.[0]?.or?.[0];
        },
    },
    watch: {
        segment () {
            this.setRules();
        },
    },
    created () {
        this.emitUpdates = debounce(this.emitUpdates, 150);
        this.setRules();
        this.emitUpdates();
    },
    async mounted () {
        await this.$nextTick();
        this.ready = true;
        if (this.isDraftEditor && this.rules.length === 0) {
            this.addRule();
        }
    },
    methods: {
        setRules () {
            const filters = this.segment?.definition?.filters || [];

            const rules = filters.reduce((rules, filter) => {
                const group = {
                    key: filter.key || uuid(),
                    or: [],
                };

                if (filter.or) {
                    group.or = filter.or.map((or) => {
                        const rule = convertRule(or);

                        return {
                            key: uuid(),
                            ...rule,
                        };
                    });
                } else {
                    const rule = convertRule(filter);
                    group.or = [
                        {
                            key: uuid(),
                            ...rule,
                        },
                    ];
                }
                rules.push(group);

                return rules;
            }, []);

            this.rules = rules;
        },
        focusNewRule (ruleIndex) {
            const { ruleList } = this.$refs;
            const ruleBlock = ruleList[ruleIndex ?? ruleList.length - 1];
            const orRules = ruleBlock.querySelectorAll('.rule-editor');
            const lastRule = orRules[orRules.length - 1];
            const firstDropdown = this.retrieveMultiSelectTrigger(lastRule);
            firstDropdown.focus();
        },
        focusNextRuleOnRemove (removingAndBlock, ruleIndex, orIndex) {
            const { ruleList } = this.$refs;

            // focus a new AND block
            if (removingAndBlock) {
                const nextAndRule = ruleList[ruleIndex + 1];

                if (nextAndRule) {
                    const firstDropdown = this.retrieveMultiSelectTrigger(nextAndRule);

                    return firstDropdown.focus(); // focus first rule of next AND block
                }

                return this.$refs.addRule.$el.focus(); // Last AND block, focus the andRule Button.
            }

            // focus next rule within an OR block
            const currentAndBlock = ruleList[ruleIndex];
            const orRules = currentAndBlock.querySelectorAll('.rule-editor');
            const nextOrRule = orRules[orIndex + 1];

            if (nextOrRule) {
                const firstDropdown = this.retrieveMultiSelectTrigger(nextOrRule);

                return firstDropdown.focus(); // focus next OR within the same AND block
            }

            return this.$refs.addOrRule[ruleIndex].$el.focus(); // Last OR rule in a block, focus OR button
        },
        async addRule () {
            this.rules.push(createEmptyRuleset());
            this.emitUpdates();
            // wait for dom updates
            await this.$nextTick();
            // In single rule mode, there's no addRule button to scroll to
            if (!this.singleRuleConfig.singleRule) {
                const target = window.innerWidth > RESPONSIVE_BREAKPOINT ? '.rules-panel' : '.pendo-drawer__body';
                const extra = window.innerWidth > RESPONSIVE_BREAKPOINT ? 16 : 96;
                scrollIntoViewIfNeeded(this.$refs.addRule.$el, this.$el.closest(target), { extra });
                this.focusNewRule();
            }
        },
        async addOrRule (ruleIndex) {
            const lastOrRule = last(this.rules[ruleIndex].or);
            const lastRuleIsSegment = lastOrRule.kind === 'segment';
            const segmentRuleLimitReached = this.segmentsUsedCount() >= SEGMENT_RULE_LIMIT;
            const newRule = lastRuleIsSegment && segmentRuleLimitReached ? createEmptyRule() : cloneDeep(lastOrRule);
            if (newRule.tagId) {
                delete newRule.tagId;
                delete newRule.value;
            }

            newRule.key = uuid();
            this.rules[ruleIndex].or.push(newRule);
            this.emitUpdates();

            // wait for dom updates
            await this.$nextTick();
            const addOrRuleButton = this.$refs.addOrRule[ruleIndex].$el;
            const target = window.innerWidth > RESPONSIVE_BREAKPOINT ? '.rules-panel' : '.pendo-drawer__body';
            scrollIntoViewIfNeeded(addOrRuleButton, this.$el.closest(target), { extra: 88 });
            this.focusNewRule(ruleIndex);
        },
        removeOrRule (ruleIndex, orIndex) {
            const rules = this.rules[ruleIndex].or;

            // if multiple `or` rules, remove `or` at index
            // otherwise, remove the entire rule
            const removingAndBlock = rules.length <= 1;

            if (!removingAndBlock) {
                this.rules[ruleIndex].or.splice(orIndex, 1);
            } else {
                this.rules.splice(ruleIndex, 1);
            }
            this.emitUpdates();

            this.focusNextRuleOnRemove(removingAndBlock, ruleIndex, orIndex);
        },
        updateOrRule (updates = {}, ruleIndex, orIndex) {
            const rule = this.rules[ruleIndex]?.or[orIndex];

            const alwaysUpdate = Object.keys(updates).filter((key) => {
                return ['filterType'].includes(key);
            });

            if (alwaysUpdate.length) {
                // always update these individual keys on the existing rule
                alwaysUpdate.forEach((key) => {
                    this.$set(rule, key, updates[key]);
                });
            }

            if (!this.ready) {
                return;
            }

            if (updates.id) {
                // if update includes an id, the user is selecting a
                // different "group" -> metadata, page, feature, guide, etc.
                // reset existing rule and start fresh
                this.$set(this.rules[ruleIndex].or, orIndex, {
                    key: rule.key,
                    ...updates,
                });
            } else {
                if (updates.eventPropertyField && updates.eventPropertyField !== rule.eventPropertyField) {
                    this.$delete(rule, 'eventPropertyValue');
                }

                const isAppIdUpdated = updates.appId && updates.appId !== rule.appId;
                if (
                    rule.id !== 'application' &&
                    !APP_SPECIFIC_AUTO_METADATA_IDS.includes(rule.id) &&
                    !getAppLevelEventRules().includes(rule.id) &&
                    isAppIdUpdated
                ) {
                    this.$set(rule, 'value', '');
                }

                // update individual keys on the existing rule
                Object.keys(updates).forEach((key) => {
                    this.$set(rule, key, updates[key]);
                });
            }

            if (updates.useEventProperty === false) {
                this.$delete(rule, 'eventPropertyName');
                this.$delete(rule, 'eventPropertyField');
                this.$delete(rule, 'eventPropertyValue');
                this.$delete(rule, 'eventPropertyOperator');
            }

            if (rule.value) {
                rule.value = removeSpecialChars(rule.value);
            }

            this.emitUpdates();
        },
        emitUpdates () {
            if (!this.ready) {
                return;
            }

            // filters are normalized on the way in to ALWAYS include an OR key
            // normalize them back to the structure that the angular Segment service expects
            const updates = this.rules.map((rule) => {
                rule.or.forEach((or) => {
                    or.$valid = isRuleValid(or);
                });

                if (rule.or.length === 1) {
                    return rule.or[0];
                }

                return rule;
            });

            // Clone before emitting, to remove the Vue reactivity that is specific to us (this component)
            this.$emit('update', cloneDeep(updates));
        },
        hasFocusGroup (rule) {
            return rule.or.some((or) => {
                return or.isFocusGroup;
            });
        },
        tooltipConfig (rule) {
            if (this.hasFocusGroup(rule)) {
                return {
                    placement: 'top',
                    content: "This rule can't be edited or deleted. Add your own rules to adjust the custom segment.",
                    arrow: true,
                };
            }

            return false;
        },
        saveSingleRule () {
            this.$emit('save');
        },
        removeSingleRule () {
            this.rules = [];
            this.emitUpdates();
            this.$emit('close');
        },
        retrieveMultiSelectTrigger (rule) {
            let trigger = rule.querySelector('div.pendo-multiselect__trigger');

            if (!trigger) {
                trigger = rule.querySelector('div.pendo-select__trigger');
            }

            return trigger;
        },
    },
};
</script>

<style scoped lang="less">
.segment-editor {
    max-width: 3000px;
    margin-right: auto;

    .segment-editor__add-rule {
        margin: 16px 0;
    }

    .segment-editor__add-or-rule {
        padding: 0 16px 16px;
    }

    .segment-editor__single-rule-save-button {
        border-top: 1px solid var(--palette-gray-40);
        padding: 16px 16px 16px;
        gap: 10px;
        display: flex;
        justify-content: flex-end;

        .pendo-button {
            width: 46px;
            height: 26px;
            min-width: 41px;
        }
    }

    .segment-editor__add-rule,
    .segment-editor__add-or-rule {
        .app.pendo-button {
            min-width: 60px;
        }
    }

    .segment-editor__add-or-rule-divider {
        margin-bottom: 16px;
    }

    .segment-editor__rule-seperator {
        display: flex;
        align-items: center;
        color: var(--palette-gray-70);
        font-weight: 600;

        .pendo-divider {
            margin-left: 8px;
        }
    }

    .segment-editor__empty-card {
        position: relative;
        height: 160px;

        :deep(.pendo-empty-state) {
            border-radius: 3px;
        }
    }

    .segment-editor__empty-card,
    .segment-editor__rule-card {
        border-radius: 3px;
        border: 1px solid var(--palette-gray-40);
        background-color: var(--palette-gray-0);
        color: var(--palette-gray-100);
        margin: 0;
        padding: 0;
    }
}

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}
</style>
